// Servicio de Cronómetro reactivo y persistente con cálculo por timestamps
// Elimina desvíos por throttling de pestañas y sincroniza con el almacenamiento local

const STORAGE_KEY = 'alerce_os_timer_state';

export function formatTimerSeconds(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hrs = String(Math.floor(s / 3600)).padStart(2, '0');
  const mins = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const secs = String(s % 60).padStart(2, '0');
  return `${hrs}:${mins}:${secs}`;
}

export class TimerServiceClass {
  constructor() {
    this.subscribers = new Set();
    this.tickerInterval = null;
    this.accumulatedSeconds = 0;
    this.isRunningState = false;
    this.startTime = null;
    this.projectId = null;
    this.projectName = '';

    this.loadState();
    if (this.isRunningState) {
      this.startTicker();
    }
  }

  loadState() {
    try {
      if (typeof localStorage === 'undefined') return;
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        this.accumulatedSeconds = Number(parsed.accumulatedSeconds) || 0;
        this.isRunningState = !!parsed.isRunning;
        this.startTime = parsed.startTime ? Number(parsed.startTime) : null;
        this.projectId = parsed.projectId ? Number(parsed.projectId) : null;
        this.projectName = parsed.projectName || '';
      }
    } catch (e) {
      console.warn('No se pudo cargar el estado del cronómetro:', e);
    }
  }

  saveState() {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        accumulatedSeconds: this.accumulatedSeconds,
        isRunning: this.isRunningState,
        startTime: this.startTime,
        projectId: this.projectId,
        projectName: this.projectName
      }));
    } catch (e) {
      console.warn('No se pudo persistir el estado del cronómetro:', e);
    }
  }

  getSeconds() {
    if (!this.isRunningState || !this.startTime) {
      return this.accumulatedSeconds;
    }
    const elapsedSinceStart = Math.floor((Date.now() - this.startTime) / 1000);
    return this.accumulatedSeconds + Math.max(0, elapsedSinceStart);
  }

  getFormattedTime() {
    return formatTimerSeconds(this.getSeconds());
  }

  isRunning() {
    return this.isRunningState;
  }

  getProjectId() {
    return this.projectId;
  }

  getProjectName() {
    return this.projectName;
  }

  setProject(projectId, projectName) {
    this.projectId = projectId ? Number(projectId) : null;
    this.projectName = projectName || '';
    this.saveState();
    this.notify();
  }

  start(projectId = null, projectName = '') {
    if (this.isRunningState) return;

    this.isRunningState = true;
    this.startTime = Date.now();
    if (projectId !== null && projectId !== undefined) {
      this.projectId = Number(projectId);
    }
    if (projectName) {
      this.projectName = projectName;
    }

    this.saveState();
    this.startTicker();
    this.notify();
  }

  pause() {
    if (!this.isRunningState) return;

    this.accumulatedSeconds = this.getSeconds();
    this.isRunningState = false;
    this.startTime = null;

    this.stopTicker();
    this.saveState();
    this.notify();
  }

  reset() {
    this.stopTicker();
    this.accumulatedSeconds = 0;
    this.isRunningState = false;
    this.startTime = null;
    this.projectId = null;
    this.projectName = '';

    this.saveState();
    this.notify();
  }

  startTicker() {
    if (this.tickerInterval) return;
    this.tickerInterval = setInterval(() => {
      this.notify();
    }, 1000);
  }

  stopTicker() {
    if (this.tickerInterval) {
      clearInterval(this.tickerInterval);
      this.tickerInterval = null;
    }
  }

  getState() {
    return {
      seconds: this.getSeconds(),
      formatted: this.getFormattedTime(),
      isRunning: this.isRunningState,
      projectId: this.projectId,
      projectName: this.projectName
    };
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    // Ejecutar callback inmediatamente con el estado actual
    try {
      callback(this.getState());
    } catch (err) {
      console.error('Error en suscriptor de cronómetro:', err);
    }

    if (this.isRunningState && !this.tickerInterval) {
      this.startTicker();
    }

    return () => {
      this.subscribers.delete(callback);
    };
  }

  notify() {
    const state = this.getState();
    this.subscribers.forEach(cb => {
      try {
        cb(state);
      } catch (err) {
        console.error('Error al notificar suscriptor de cronómetro:', err);
      }
    });
  }
}

export const TimerService = new TimerServiceClass();
